/**
 * DIE BAUSTEINE DES LIEFERANTEN -- Bewertung und Zahlungsplan.
 *
 * Sie standen bis zum Lieferantenportal in `ArticleLogistics.tsx`, weil es
 * nur EINEN Ort gab, an dem man einen Lieferanten beurteilt: den Einkauf
 * eines Artikels. Jetzt gibt es zwei -- den Artikel und die
 * Lieferantenakte -- und zwei Kopien derselben Sterne waeren zwei
 * Wahrheiten darueber, wie eine Note aussieht und was ein halber Stern
 * bedeutet.
 *
 * Verschoben, nicht neu geschrieben: dieselben Klassennamen, dieselbe
 * Auszeichnung, dieselben Regeln. Der Pruefstand misst sie an beiden
 * Orten mit denselben Merkmalen.
 */
import { PAYMENT_METHODS, SUPPLIER_RATING_CRITERIA } from "@homebase/shared";
import type {
  MetaResponse, PaymentTerms, PaymentTermsLevel, PurchasingDetails, SupplierRating,
} from "@homebase/shared";
import type { I18n } from "../i18n/index.tsx";

/* ── Die Lieferantenbewertung ─────────────────────────────────────────────
 *
 * VIER NOTEN, EIN DURCHSCHNITT, KEINE ZWEITE WAHRHEIT.
 *
 * Die Skala reicht von 1,0 bis 5,0 in halben Schritten. `null` heisst NICHT
 * BEWERTET und ist ausdruecklich etwas anderes als 1,0 -- „noch nie
 * beurteilt" gegen „beurteilt und fuer schlecht befunden". Aus einem leeren
 * Wert wird hier niemals eine Null.
 *
 * Die Gesamtbewertung ist der Mittelwert der VORHANDENEN Noten. Sie wird
 * gerechnet und nicht gespeichert; sie ist nicht bedienbar und traegt kein
 * Feld. Ist nichts bewertet, steht dort nichts -- kein 0,0 und kein „—",
 * das wie eine Note aussieht.
 */
/*
 * DIE KRITERIEN KOMMEN AUS DEM GETEILTEN PAKET -- eine Liste, nicht zwei.
 *
 * Sie stand hier bis zum fuenften Kriterium ein zweites Mal. Solange beide
 * Listen dieselben vier Namen trugen, fiel das nicht auf; beim Nachziehen
 * faellt es sofort auf, weil man es zweimal tun muss. Genau das ist der
 * Grund, es nicht zweimal zu schreiben.
 */
export const BEWERTUNGSFELDER = SUPPLIER_RATING_CRITERIA;
export type Bewertungsfeld = (typeof BEWERTUNGSFELDER)[number];
/*
 * Die Beschriftungen AUSGESCHRIEBEN, nicht zusammengesetzt.
 *
 * `t(\`bew.${k}\`)` waere kuerzer und fuer `checki18n` unsichtbar: die
 * Pruefung „kein unbenutzter Schluessel" liest den Quelltext und findet
 * einen zusammengesetzten Namen nicht. Vier Zeilen sind der Preis dafuer,
 * dass ein vergessener Schluessel auffaellt.
 */
const BEWERTUNGSLABEL = {
  production: "bew.production", reliability: "bew.reliability",
  communication: "bew.communication", price: "bew.price",
  paymentTerms: "bew.paymentTerms",
} as const satisfies Record<Bewertungsfeld, string>;
export type Bewertung = Record<Bewertungsfeld, string | null>;

export const ausBewertung = (r: SupplierRating): Bewertung => ({
  production: r.production, reliability: r.reliability,
  communication: r.communication, price: r.price,
  paymentTerms: r.paymentTerms,
});
export const bewertungGleich = (a: Bewertung, b: Bewertung): boolean =>
  BEWERTUNGSFELDER.every((k) =>
    (a[k] === null) === (b[k] === null) && (a[k] === null || Number(a[k]) === Number(b[k])));

/**
 * Fuenf Sterne, der letzte notfalls halb.
 *
 * DER HALBE STERN IST GEOMETRISCH HALB, nicht „½" und kein zweites Symbol.
 * Zwei uebereinanderliegende Sterne: der untere grau, der obere gold und auf
 * die Haelfte beschnitten. Das ergibt bei jeder Schriftgroesse denselben
 * Eindruck und braucht keine Grafik.
 *
 * `aria-hidden`, weil daneben die Zahl steht. Ein Vorlesewerkzeug soll „4,5"
 * hoeren und nicht zehn Sternzeichen.
 */
/**
 * Der Durchschnitt der VORHANDENEN Noten -- nicht der fuenf.
 *
 * Fehlende Kriterien als 0 mitzurechnen waere eine Bewertung, die niemand
 * abgegeben hat. Ohne Note gibt es keinen Durchschnitt (`null`). Eine Stelle,
 * zwei Leser: die Bewertungsbox im Lieferantenportal und die verdichtete
 * Angabe „AGC ★ 4,1" in der Produktakte.
 */
function mittelwert(werte: readonly string[]): number | null {
  return werte.length === 0 ? null : werte.reduce((a, b) => a + Number(b), 0) / werte.length;
}

/** Die Gesamtbewertung eines gespeicherten Lieferantenstands. */
export function gesamtbewertung(r: SupplierRating): number | null {
  return mittelwert(BEWERTUNGSFELDER.map((k) => r[k]).filter((v): v is string => v !== null));
}

export function Sterne({ wert }: { wert: number | null }): React.JSX.Element {
  return (
    <span className="bew__sterne" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => {
        const anteil = wert === null ? 0 : Math.min(1, Math.max(0, wert - (i - 1)));
        return (
          <span className="bew__stern" key={i}>
            <span className="bew__sternleer">★</span>
            <span className="bew__sternvoll" style={{ width: `${anteil * 100}%` }}>★</span>
          </span>
        );
      })}
    </span>
  );
}

/**
 * Eine Bewertungszeile -- im Lesemodus Sterne und Zahl, im
 * Bearbeitungsmodus dieselben Sterne, nur anklickbar.
 *
 * ZWEI SCHALTFLAECHEN JE STERN statt einer Klickmessung auf Pixelebene.
 * Das Ergebnis ist dasselbe -- linke Haelfte des vierten Sterns ergibt 3,5,
 * rechte 4,0 --, aber es funktioniert auch mit der Tastatur, meldet dem
 * Vorlesewerkzeug einen Wert statt einer Position und braucht keine
 * Annahmen ueber Sternbreiten.
 */
function BewertungsZeile({ label, wert, bearbeitet, speichert, i18n, setWert }: {
  label: string;
  wert: string | null;
  bearbeitet: boolean;
  speichert: boolean;
  i18n: I18n;
  setWert: (v: string | null) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const zahl = wert === null ? null : Number(wert);
  return (
    <div className="bew__zeile">
      <span className="bew__label">{label}</span>
      {bearbeitet ? (
        <span className="bew__wahl">
          <Sterne wert={zahl} />
          <span className="bew__treffer">
            {/*
              * DIE SKALA BEGINNT BEI 1,0 -- nicht bei 0,5.
              *
              * Zehn Haelften, aber nur neun Werte: die linke Haelfte des
              * ERSTEN Sterns ergibt ebenfalls 1,0. Ein halber erster Stern
              * waere 0,5 und damit ausserhalb der Skala; ihn unklickbar zu
              * machen hiesse, ein Stueck des Sterns tot zu legen, und wer
              * dorthin zielt, meint die schlechteste Note.
              */}
            {[1, 2, 3, 4, 5].flatMap((i) => [Math.max(1, i - 0.5), i]).map((v, idx) => (
              <button type="button" key={idx} className="bew__halb" disabled={speichert}
                aria-label={`${label}: ${v.toLocaleString(i18n.lang, { minimumFractionDigits: 1 })}`}
                aria-pressed={zahl === v}
                onClick={() => setWert(v.toFixed(1))} />
            ))}
          </span>
        </span>
      ) : <Sterne wert={zahl} />}
      <span className="bew__wert">
        {zahl === null
          ? <span className="bew__leer">{t("bew.notRated")}</span>
          : zahl.toLocaleString(i18n.lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      </span>
      {/* Der Weg ZURUECK zu „nicht bewertet". Ohne ihn waere eine einmal
          vergebene Note nicht mehr zuruecknehmbar, und 1,0 muesste als
          Ersatz herhalten -- genau die Verwechslung, die das Modell
          vermeidet. */}
      {bearbeitet && (
        <button type="button" className="bew__weg" disabled={speichert || zahl === null}
          onClick={() => setWert(null)}>{t("bew.clear")}</button>
      )}
    </div>
  );
}

/**
 * Die Box „Lieferantenbewertung".
 *
 * Sie gehoert dem LIEFERANTEN. Zwei Artikel desselben Partners zeigen
 * dieselbe Bewertung, und wer sie an einem Artikel aendert, aendert sie fuer
 * alle -- das ist der Zweck und keine Nebenwirkung.
 */
export function BewertungsBox({ gespeichert, bewertung, bearbeitet, speichert, i18n, setWert }: {
  /** Der gespeicherte Stand -- fuer die Herkunftszeile. */
  gespeichert: SupplierRating;
  bewertung: Bewertung;
  bearbeitet: boolean;
  speichert: boolean;
  i18n: I18n;
  setWert: (k: Bewertungsfeld, v: string | null) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const werte = BEWERTUNGSFELDER.map((k) => bewertung[k]).filter((v): v is string => v !== null);
  /*
   * DER DURCHSCHNITT DER VORHANDENEN -- nicht der vier.
   *
   * Sind nur zwei Kriterien bewertet, ist der Mittelwert der beiden die
   * Antwort. Die fehlenden als 0 mitzurechnen waere eine Bewertung, die
   * niemand abgegeben hat. Ist gar nichts bewertet, gibt es keinen
   * Durchschnitt -- und dann steht dort auch keiner.
   */
  const schnitt = mittelwert(werte);

  return (
    <section className="log__group bew">
      <h4 className="log__grouptitle">{t("bew.title")}</h4>
      <div className="bew__zeilen">
        {BEWERTUNGSFELDER.map((k) => (
          <BewertungsZeile key={k} label={t(BEWERTUNGSLABEL[k])} wert={bewertung[k]}
            bearbeitet={bearbeitet} speichert={speichert} i18n={i18n}
            setWert={(v) => setWert(k, v)} />
        ))}
      </div>
      {/*
        * DIE GESAMTBEWERTUNG IST GERECHNET UND DESHALB NICHT BEDIENBAR.
        *
        * Sie traegt kein Feld und keine Schaltflaeche, auch nicht im
        * Bearbeitungsmodus -- sie folgt den vier Noten so, wie das Volumen
        * den Aussenmassen folgt. Gerundet wird nur die ANZEIGE: 4,25 steht
        * als 4,3 da, gerechnet wird mit 4,25.
        */}
      <div className="bew__gesamt">
        <span className="bew__gesamtlabel">
          {t("bew.overall")}
          <span className="bew__gesamthinweis">
            {werte.length === 0
              ? t("bew.overallNone")
              : t("bew.overallFrom", { n: werte.length })}
          </span>
        </span>
        <Sterne wert={schnitt} />
        <span className="bew__gesamtwert">
          {schnitt === null
            ? <span className="bew__leer">{t("bew.notRated")}</span>
            : schnitt.toLocaleString(i18n.lang,
                { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
        </span>
      </div>
      {/*
        * EINE NOTE OHNE URHEBER IST KEINE NOTE.
        *
        * Gezeigt wird der LETZTE Eingriff -- keine Historie. Steht keine
        * Bewertung, steht auch keine Herkunft da; die Datenbank erzwingt,
        * dass beides zusammen kommt und zusammen geht.
        */}
      {gespeichert.ratedAt !== null && (
        <p className="bew__herkunft">
          {t("bew.ratedBy", {
            wer: gespeichert.ratedBy ?? "—",
            wann: gespeichert.ratedAt.slice(0, 10),
          })}
        </p>
      )}
    </section>
  );
}

// ── Zahlungsbedingungen ─────────────────────────────────────────────────
//
// Der Plan gilt entweder als Standard des Lieferanten oder als Abweichung
// fuer diese eine Beziehung. Die Abweichung ersetzt den Standard als
// GANZES -- nie stufenweise.

/** Eine Stufe im Entwurf. Zeichenketten, wie ueberall in dieser Datei. */
export interface Zahlstufe {
  readonly anteil: string;
  readonly ereignis: string;
  readonly zusatz: string;
  readonly frist: string;
  /** Wie die Stufe im Vertrag heisst (0094). Leer heisst: nicht benannt. */
  readonly bezeichnung: string;
  /** Der VEREINBARTE Weg (0094). Leer heisst: nicht vereinbart. */
  readonly weg: string;
}
export interface Zahlentwurf {
  readonly ebene: PaymentTermsLevel;
  readonly stufen: readonly Zahlstufe[];
}

/**
 * Der MASSGEBLICHE Plan: die Abweichung, sonst der Standard, sonst keiner.
 *
 * Das ist die Vorrangregel an genau einer Stelle im Frontend. Sie steht
 * hier und nicht in jeder Ansicht, die einen Plan zeigt.
 */
export const massgeblich = (p: PurchasingDetails): PaymentTerms | null =>
  p.paymentTerms ?? p.supplier.paymentTerms ?? null;

export const ausZahlplan = (p: PurchasingDetails): Zahlentwurf => {
  const plan = massgeblich(p);
  return {
    /* Ohne Plan ist der Standard des Lieferanten der Normalfall -- eine
       Abweichung setzt voraus, dass es etwas gibt, wovon sie abweicht. */
    ebene: plan?.level ?? "supplier",
    stufen: (plan?.stages ?? []).map((x) => ({
      anteil: kurzerAnteil(x.sharePercent),
      ereignis: x.event,
      zusatz: x.eventNote ?? "",
      frist: x.offsetDays === null ? "" : String(x.offsetDays),
      bezeichnung: x.label ?? "",
      weg: x.paymentMethod ?? "",
    })),
  };
};
export const zahlplanGleich = (a: Zahlentwurf, b: Zahlentwurf): boolean =>
  a.ebene === b.ebene && a.stufen.length === b.stufen.length
  && a.stufen.every((x, i) => {
    const y = b.stufen[i]!;
    /* BESCHRIFTUNG UND WEG GEHOEREN IN DEN VERGLEICH. Ohne sie bliebe
       eine Aenderung, die nur die Stufe umbenennt oder den Weg
       umstellt, fuer „ungespeicherte Aenderungen" unsichtbar -- und
       ginge beim Verlassen der Maske verloren. */
    return x.ereignis === y.ereignis && x.zusatz.trim() === y.zusatz.trim()
        && x.frist.trim() === y.frist.trim()
        && x.bezeichnung.trim() === y.bezeichnung.trim()
        && x.weg === y.weg
        && Number(x.anteil.replace(",", ".")) === Number(y.anteil.replace(",", "."));
  });

/**
 * „30.00" wird zu „30", „33.33" bleibt „33,33".
 *
 * Die Datenbank speichert zwei Nachkommastellen, weil es Drittelungen
 * gibt. Sie in „30,00 %" zu zeigen waere eine Genauigkeit, die niemand
 * gemeint hat.
 */
function kurzerAnteil(roh: string): string {
  const n = Number(roh);
  return Number.isInteger(n) ? String(n) : String(n);
}

/**
 * Der Plan als ein Satz: „30 % Deposit · 70 % After QC / vor B/L".
 *
 * Gerechnet, nicht gespeichert -- es gibt keine Kurzform in der Datenbank,
 * und es soll auch keine geben.
 */
export function zahlplanSatz(plan: PaymentTerms, i18n: I18n): string {
  /*
   * ── 100 % SIND KEINE ANZAHLUNG ─────────────────────────────────
   *
   * DER BEFUND DER BROWSER-ABNAHME 002 (Abschnitt 6). Bei voller
   * Vorkasse stand da „100 % Anzahlung". Eine Anzahlung ist der TEIL,
   * den man vorab zahlt -- wer alles vorab zahlt, zahlt keine
   * Anzahlung, sondern den ganzen Betrag bei Auftragserteilung.
   *
   * NUR DER SATZ AENDERT SICH. Der Termincode bleibt `deposit`, die
   * Stufe bleibt dieselbe Zeile, und gerechnet wird unveraendert.
   * Dies ist eine Frage der Sprache und keine der Fachlogik.
   *
   * UND NUR FUER DIESE EINE KOMBINATION. Eine einzige Stufe mit
   * 100 % auf einem ANDEREN Ereignis -- „100 % nach Rechnung" --
   * ist richtig formuliert und bleibt, wie sie ist.
   */
  const vollvorkasse = plan.stages.length === 1
    && Number(plan.stages[0]!.sharePercent) === 100
    && plan.stages[0]!.event === "deposit";
  return plan.stages.map((st) => {
    const anteil = Number(st.sharePercent).toLocaleString(i18n.lang, {
      maximumFractionDigits: 2,
    });
    /*
     * DIE BESCHRIFTUNG STEHT VORAN, WENN ES EINE GIBT -- und das
     * Ereignis bleibt daneben stehen.
     *
     * Sie ERSETZT es nicht: „Anzahlung bei Auftrag" sagt, was im
     * Vertrag steht, und `deposit` sagt, woran die Faelligkeit haengt.
     * Nur die Beschriftung zu zeigen hiesse, die auswertbare Angabe zu
     * verbergen -- und wer zwei Stufen „Rest" nebeneinander sieht,
     * weiss nicht, welche wovon.
     */
    const kopf = `${anteil} % ${vollvorkasse
      ? i18n.t("ek.termsOnOrder") : i18n.tCode("payEvent", st.event)}`;
    const teile = [st.label === null || st.label === ""
      ? kopf : `${st.label} (${kopf})`];
    if (st.offsetDays !== null) teile.push(i18n.t("ek.termsAfterDays", { n: st.offsetDays }));
    if (st.eventNote !== null && st.eventNote !== "") teile.push(st.eventNote);
    /* Der Weg nur, wenn er vereinbart ist. „nicht vereinbart"
       dazuzuschreiben machte jeden Satz laenger und sagte nichts. */
    if (st.paymentMethod !== null) {
      teile.push(i18n.tCode("payMethod", st.paymentMethod));
    }
    return teile.join(" / ");
  }).join(" · ");
}

/**
 * Der Zahlungsplan im Bearbeitungsmodus.
 *
 * WAS DIESER EDITOR NICHT TUT
 *
 *   * Er rechnet nichts glatt. Ergeben die Anteile nicht 100 %, sagt er es
 *     und laesst die Eingabe stehen -- er ergaenzt keine Restprozente.
 *     Eine automatische Korrektur waere eine Vereinbarung, die niemand
 *     getroffen hat.
 *   * Er wechselt die Ebene nicht heimlich. Besteht fuer diese Beziehung
 *     bereits eine Abweichung, bearbeitet man DIESE; der Weg zurueck zum
 *     Standard ist ihre Ruecknahme und nicht ein Umschalter, der sie still
 *     stehen liesse und wirkungslos machte.
 */
export function Zahlplaneditor({ entwurf, ereignisse, hatStandard, speichert, i18n, setEntwurf }: {
  entwurf: Zahlentwurf;
  ereignisse: MetaResponse["paymentEvents"];
  hatStandard: boolean;
  speichert: boolean;
  i18n: I18n;
  setEntwurf: (e: Zahlentwurf) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const stufen = entwurf.stufen;
  const setzeStufe = (i: number, f: Partial<Zahlstufe>): void => setEntwurf({
    ...entwurf, stufen: stufen.map((x, j) => (j === i ? { ...x, ...f } : x)),
  });
  const brauchtFrist = (code: string): boolean =>
    ereignisse.find((e) => e.code === code)?.needsOffset === true;

  /*
   * DIE SUMME WIRD GERECHNET, NICHT GESPEICHERT -- und in Hundertsteln,
   * damit 33,33 + 33,33 + 33,34 verlaesslich 100 ergibt.
   */
  const hundertstel = stufen.reduce(
    (a, x) => a + Math.round(Number(x.anteil.replace(",", ".") || "0") * 100), 0);
  const summe = (hundertstel / 100).toLocaleString(i18n.lang, { maximumFractionDigits: 2 });

  return (
    <div className="ek__planedit">
      <ul className="ek__stufen">
        {stufen.map((st, i) => (
          <li className="ek__stufe" key={i}>
            <input className="log__input ek__anteil" inputMode="decimal" value={st.anteil}
              disabled={speichert} aria-label={t("ek.stageShare")}
              onChange={(e) => setzeStufe(i, { anteil: e.target.value })} />
            <span className="ek__prozent">%</span>
            <select className="log__input ek__ereignis" value={st.ereignis} disabled={speichert}
              aria-label={t("ek.stageEvent")}
              onChange={(e) => setzeStufe(i, {
                ereignis: e.target.value,
                /* Eine Frist an einem Ereignis, das keine kennt, weist der
                   Server ab. Sie hier stehen zu lassen hiesse, den Fehler
                   erst beim Speichern zu zeigen. */
                frist: brauchtFrist(e.target.value) ? st.frist : "",
              })}>
              {ereignisse.map((ev) => (
                <option key={ev.code} value={ev.code}>{i18n.tCode("payEvent", ev.code)}</option>
              ))}
            </select>
            {brauchtFrist(st.ereignis) && (
              <input className="log__input ek__frist" inputMode="numeric" value={st.frist}
                disabled={speichert} aria-label={t("ek.stageOffset")}
                placeholder={t("ek.stageOffset")}
                onChange={(e) => setzeStufe(i, { frist: e.target.value })} />
            )}
            <input className="log__input log__input--text ek__zusatz" value={st.zusatz}
              disabled={speichert} aria-label={t("ek.stageNote")} placeholder={t("ek.stageNote")}
              onChange={(e) => setzeStufe(i, { zusatz: e.target.value })} />
            {/*
              * BESCHRIFTUNG UND WEG (0094) -- je Stufe, nicht je Plan.
              *
              * Sie stehen je Stufe, weil sie je Stufe verschieden sind:
              * die Anzahlung geht per Ueberweisung, die Restzahlung
              * gegen Akkreditiv. Ein Feld am Plan koennte nur eines
              * von beiden tragen.
              */}
            <input className="log__input log__input--text ek__bez" value={st.bezeichnung}
              disabled={speichert} aria-label={t("ek.stageLabel")}
              placeholder={t("ek.stageLabel")}
              onChange={(e) => setzeStufe(i, { bezeichnung: e.target.value })} />
            <select className="log__input ek__weg" value={st.weg} disabled={speichert}
              aria-label={t("ek.stageMethod")}
              onChange={(e) => setzeStufe(i, { weg: e.target.value })}>
              {/* Leer ist die Vorgabe und bleibt es: „Ueberweisung"
                  vorzuwaehlen hiesse, eine Vereinbarung zu erfinden. */}
              <option value="">{t("ek.stageMethodNone")}</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{i18n.tCode("payMethod", m)}</option>
              ))}
            </select>
            <button type="button" className="ek__stufeweg" disabled={speichert}
              aria-label={t("ek.stageRemove")}
              onClick={() => setEntwurf({
                ...entwurf, stufen: stufen.filter((_, j) => j !== i) })}>×</button>
          </li>
        ))}
      </ul>
      <div className="ek__planaktionen">
        <button type="button" className="btn btn--small" disabled={speichert}
          onClick={() => setEntwurf({ ...entwurf, stufen: [...stufen, {
            anteil: "", ereignis: ereignisse[0]?.code ?? "deposit", zusatz: "", frist: "",
            bezeichnung: "", weg: "",
          }] })}>{t("ek.stageAdd")}</button>
        {stufen.length > 0 && (
          <span className={hundertstel === 10000 ? "ek__summe" : "ek__summe ek__summe--offen"}>
            {t("ek.stageSum", { n: summe })}
          </span>
        )}
      </div>
      {/*
        * DIE EBENE -- sichtbar und nur dort waehlbar, wo sie eindeutig ist.
        *
        * Besteht bereits eine Abweichung fuer diese Beziehung, gibt es
        * nichts zu waehlen: bearbeitet wird sie. Wer zum Standard zurueck
        * will, nimmt sie zurueck -- dann ist auch klar, was danach gilt.
        */}
      {entwurf.ebene === "article" ? (
        <p className="ek__planebene">
          {t("ek.termsEditingArticle")}
          <button type="button" className="bew__weg" disabled={speichert}
            onClick={() => setEntwurf({ ...entwurf, stufen: [] })}>
            {t("ek.termsDropArticle")}
          </button>
        </p>
      ) : (
        <p className="ek__planebene">
          {t("ek.termsEditingSupplier")}
          {stufen.length > 0 && (
            <label className="ek__nurartikel">
              <input type="checkbox" disabled={speichert} checked={false}
                onChange={() => setEntwurf({ ...entwurf, ebene: "article" })} />
              {t("ek.termsOnlyThis")}
            </label>
          )}
          {hatStandard && <span className="ek__planhinweis">{t("ek.termsSupplierWide")}</span>}
        </p>
      )}
    </div>
  );
}

